/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
/* tslint:disable: no-duplicate-imports && max-line-length */
import { ArrayExtensions, getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';
import {
    getTelemetryObject,
    ILabeledDropdownOnChangeNotification,
    ILabeledDropdownOption,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    UncontrolledPagination,
    isMobile,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import { CategoryHierarchy as CategoryHierarchyData, IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import MsDyn365 from '@msdyn365-commerce/core';
import {
    format,
    ProductPrice,
    ProductRefinerValue,
    ProjectionDomain,
    SortColumn,
    TextValueTranslation
} from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    AffiliationLoyaltyTier,
    CustomerAffiliation,
    ProductSearchResult
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classnames from 'classnames';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import { computed, observable, reaction, transaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import UiLabeledDropdown from '../../Utilities/labeled-dropdown/ui-labeled-dropdown/UiLabeledDropdown';
import { IMenuItemData } from '../mfrm-navigation-menu/mfrm-navigation-menu.data';
import {
    buildListPageUrl,
    getCollectionProducts,
    getCurrentUrl,
    GetFullProductsByCollectionInput,
    parseQueryParam,
    sortOptions
} from './actions';

import {
    ErrorMessage,
    FeatureSearchResultItem,
    getUpdatedRefinementCriteria,
    IChoiceSummaryClickNotification,
    IRefineItemCommonProps,
    IRefineItemToggleNotification,
    ISearchResultModalViewProps,
    isMatchingRefinementCriterion,
    isRangeType,
    Link,
    ModalToggle,
    SearchResultModal,
    Separator,
    Title
} from './components';
import ChoiceSummary from './components/choice-summary';
import RefineSubmenu from './components/refine-submenu';
import { IMfrmSearchResultContainerData } from './mfrm-search-result-container.data';
import { IMfrmSearchResultContainerProps } from './mfrm-search-result-container.props.autogenerated';
import { SrpBreadcrumbPageInput } from '../../actions/srp-breadcrumb-page.action';
import { getSortedVirtualRefinerConfig, getVirtualRefinerNames, regExSpecialChars, removeKeywords } from '../../Utilities/plp-url-utils';
import getProducts from './actions/get-full-products-by-collection';
import { ErrorBoundary } from '../../common/components/error-boundary';
export interface ISearchResultContainerViewProps extends IMfrmSearchResultContainerProps<IMfrmSearchResultContainerData> {
    products?: React.ReactNode;
    className?: string;
    SearchResultContainer: IModuleProps;
    TitleViewProps: ITitleViewProps;
    categoryHierarchy: ICategoryHierarchyViewProps;
    pagination?: React.ReactNode;
    ProductsContainer: INodeProps;
    ProductSectionContainer: INodeProps;
    refineMenu: IRefineMenuViewProps;
    sortByOptions: ISortByViewProps;
    choiceSummary?: React.ReactNode;
    clearAllBtn?: React.ReactNode;
    modalToggle: React.ReactNode;
    searchResultModal: ISearchResultModalViewProps;
    isMobile: boolean;
    CategoryNavContainer: INodeProps;
    RefineAndProductSectionContainer: INodeProps;
    errorMessage: React.ReactNode;
    FeatureSearchContainer: INodeProps;
    similarLookProduct?: React.ReactNode;
    productPrices?: ProductPrice[];
    showCategoryBanner?: boolean;
    categoryBannerInd?: number;
    matcherBannerSlotInd?: number;
    addedProducts: [];
    onlyNewProducts: [];
    isMattressesCategory: boolean | undefined;
    refinerRedirect: boolean;
    isCategoryPage: boolean;
    categoryNameFromSlug: string | undefined;
    seoDataEntry: object | undefined;
    activeRefiners: [];
    showProductsLoader: boolean;
    itemsPerPageInitialState: number;
    getScrolledProducts(): Promise<void>;
}

export interface ITitleViewProps {
    TitleContainer: INodeProps;
    title: ISearchResultTitle;
    totalItems?: number;
}

export interface IRefineItemURLParts {
    origin: string;
    categoryPart: string;
    categoryId: string | undefined;
}

export interface IRefineMenuViewProps {
    RefineMenuContainer: INodeProps;
    RefinerSectionContainer: INodeProps;
    refiners?: React.ReactNode[];
    applyBtn?: React.ReactNode[];
    refinerLabel: string;
}

export interface ICategoryHierarchyViewProps {
    categoryHierarchyList?: React.ReactNode[];
    categoryHierarchySeparator?: React.ReactNode;
    CategoryHierarchyContainer: INodeProps;
}

export interface ISortByViewProps {
    sortByDropDown?: React.ReactNode;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Title component for search result container
 */
export interface ISearchResultTitle {
    searchPageTitle?: React.ReactNode;
    titlePrefix?: React.ReactNode;
    titleText?: React.ReactNode;
    titleCount?: React.ReactNode;
}

export interface ISearchResultContainerState {
    sortingState: ISortByCollectionState;
    modalIsOpen: boolean;
    showCategoryBanner: boolean;
    categoryBannerIndex: number;
    matcherBannerSlotInd: number;
    lastBannerID: string;
    // TODO: Set proper type
    // tslint:disable-next-line:no-any
    addedProducts: any;
    onlyNewProducts: any;
    infiniteScrollPage: number;
    scrollHeightPrevValue: number;
    totalSkipProductsValue: number;
    allSizeRefinerValues: (string | ProductRefinerValue | undefined)[];
    allVirtualRefiners: (string | undefined)[];
    isPriceRefinerDisabled: boolean;
    isRefinerRedirectEnabled: boolean;
    showProductsLoader: boolean;
    sortedOptions: SortColumn[];
    itemsPerPageInitialState: number;
}
interface ISortByCollectionState {
    selectedSortByOption: ILabeledDropdownOption;
    pending: boolean;
}
/**
 *
 * SearchResultContainer component
 * @extends {React.PureComponent<IMfrmSearchResultContainerProps<IMfrmSearchResultContainerData>>}
 */
@observer
export default class SearchResultContainer extends React.PureComponent<
    IMfrmSearchResultContainerProps<IMfrmSearchResultContainerData>,
    ISearchResultContainerState
> {
    @computed get isMobile(): boolean {
        return this._viewport === 'xs' || this._viewport === 'sm';
    }

    private sortByDropdownOptions: ILabeledDropdownOption[] = [
        { key: sortOptions.sortByOptionRelevanceDesc, value: this.props.resources.sortByOptionRelevanceDesc },
        { key: sortOptions.sortByOptionPriceAsc, value: this.props.resources.sortByOptionPriceAsc },
        { key: sortOptions.sortByOptionPriceDesc, value: this.props.resources.sortByOptionPriceDesc },
        { key: sortOptions.sortByOptionRatingDesc, value: this.props.resources.sortByOptionRatingDesc }
    ];
    private _refineItemCommonProps: IRefineItemCommonProps;
    private _pageType: string | undefined = this.props.context.request.urlTokens.pageType;
    private _isCustomizedRefiner: boolean =
        this.props.context.request.app.config.urlModifications && this.props.context.request.app.config.urlModifications?.length > 0;
    @observable
    private _viewport: GridSize = 'lg';
    private _modalToggleRef: React.RefObject<HTMLButtonElement>;
    private _sortAndFilterContainerRef: React.RefObject<UiLabeledDropdown>;
    private telemetryContent: ITelemetryContent;
    private productPrices: ProductPrice[];

    private currentNavMenu: IMenuItemData[] = [];
    private isMattressesCategory: boolean | undefined;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && ArrayExtensions.hasElements(nameTranslations)) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }
    // tslint:disable-next-line:max-func-body-length
    constructor(props: IMfrmSearchResultContainerProps<IMfrmSearchResultContainerData>, state: ISearchResultContainerState) {
        super(props);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Check required.
        this._viewport = props.context.request?.device?.Type === 'Mobile' ? 'xs' : 'lg';
        this._modalToggleRef = React.createRef<HTMLButtonElement>();
        this._sortAndFilterContainerRef = React.createRef<UiLabeledDropdown>();
        this._toggleModal = this._toggleModal.bind(this);
        this._updateViewport = this._updateViewport.bind(this);
        this._getCategoryBanner = this._getCategoryBanner.bind(this);
        this._updateMattressMatcherBanner = this._updateMattressMatcherBanner.bind(this);
        this.productPrices = [];
        this.state = {
            sortingState: {
                pending: false,
                selectedSortByOption: this.sortByDropdownOptions[0]
            },
            modalIsOpen: false,
            showCategoryBanner: false,
            categoryBannerIndex: -1,
            matcherBannerSlotInd: -1,
            lastBannerID: '',
            addedProducts: [],
            onlyNewProducts: [],
            infiniteScrollPage: 1,
            scrollHeightPrevValue: 0,
            totalSkipProductsValue: 0,
            allSizeRefinerValues: [],
            isPriceRefinerDisabled: false,
            allVirtualRefiners: [],
            isRefinerRedirectEnabled: false,
            sortedOptions: [],
            showProductsLoader: true,
            itemsPerPageInitialState: props.config.itemsPerPage || 10
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.props.data.products.then(products => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.props.data.listPageState.then(listPageState => {
                listPageState.activeProducts = [...products.products];
                listPageState.totalProductCount = products.count;
                listPageState.activeFilters = [...products.virtualRefiners];
                listPageState.activeVirtualFilters = [...products.minusVirtualRefiner];
            });
        });

        this.isMattressesCategory = this.props?.data.category.result?.ExtensionProperties?.find(
            attr => attr.Key === 'SizeRefinementEnable'
        )?.Value?.BooleanValue;
        const { placeholderTextMax, minLabel, maxLabel, rangeNameFormat } = this.props.resources;
        const locale = this.props.context.request.locale;
        const telemetry = this.props.telemetry;
        const validationErrorNaN = this.props.resources.validationErrorNotNumber;
        const validationErrorRange = this.props.resources.validationErrorNotRange;
        this._refineItemCommonProps = {
            telemetry,
            locale,
            placeholderTextMax,
            minLabel,
            maxLabel,
            rangeNameFormat,
            validationErrorNaN,
            validationErrorRange
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        if (this.props.context.request.query?.productId) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.props.data.featureProduct.then(products => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.props.data.listPageState.then(listPageStateFeature => {
                    listPageStateFeature.featureProduct = products;
                });
            });
        }
        this._updateViewport();
    }
    // VSI Customization - START - 23797
    public getPagePath = (): string => {
        return MsDyn365.isBrowser
            ? window.location.href.split(location.host)[1].includes('?')
                ? window.location.href.split(location.host)[1].split('?')[0]
                : window.location.href.split(location.host)[1]
            : '';
    };
    public getCategoryName = (): string | undefined => {
        const { data, context } = this.props;
        let categoryName: string | undefined = '';
        if (context && context.request && context.request.query && context.request.query.q) {
            categoryName = `'${context.request.query.q}'`;
        } else {
            categoryName =
                (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);
        }
        return categoryName;
    };
    public getSkipProductValue = (): number => {
        let skipProducts: number = 0;
        const viewPortCode = this._getViewport();
        if (viewPortCode === 'xs' || viewPortCode === 'sm' || viewPortCode === 'md') {
            skipProducts = 1;
        } else if (viewPortCode === 'lg') {
            if (MsDyn365.isBrowser && window.innerWidth) {
                if (window.innerWidth >= 1070 && window.innerWidth < 1112) {
                    skipProducts = 2;
                } else {
                    skipProducts = 3;
                }
            }
        } else if (viewPortCode === 'xl') {
            skipProducts = 3;
        }
        return skipProducts;
    };

    // VSI Customization - END - 23797
    public componentDidMount(): void {
        const extensionProperties = this.props.data.products.result?.products[0].ExtensionProperties;
        const redirectUrl = extensionProperties && extensionProperties?.find(obj => obj?.Key === 'RedirectURL')?.Value?.StringValue;

        if (MsDyn365.isBrowser && redirectUrl) {
            const url = window.location.href;
            if (url !== redirectUrl) {
                document.location.href = redirectUrl ? redirectUrl : '';
            }
        }
        // Initalization of list page
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.props.data.products.then(products => {
            this.setState({
                showProductsLoader: false
            });

            // Initalization of list page
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.props.data.listPageState.then(listPageState => {
                if (this._pageType === 'Category') {
                    listPageState.pageType = 'Category';
                } else {
                    listPageState.pageType = 'Search';
                }

                let querySorting: SortColumn[] = [];

                if (this.props.context.request.query && this.props.context.request.query.sorting) {
                    querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
                }

                listPageState.currentPageNumber =
                    (this.props.context.request.query && +this.props.context.request.query.skip / (this.props.config.itemsPerPage || 10)) ||
                    0;
                listPageState.sortingCritera = { Columns: querySorting };
                listPageState.pageSize = this.props.config.itemsPerPage || 10;
                listPageState.activeProducts = [...products.products];
                listPageState.totalProductCount = products.count;
                // Todo: will remove as functionality completes 06/06/2021
                // VSI Customization 7924 Start
                listPageState.activeFilters = [...products.virtualRefiners];
                listPageState.activeVirtualFilters = [...products.minusVirtualRefiner];
                // VSI Customization 7924 End
                // Initialize reaction based on listPageState properties
                reaction(
                    () => {
                        return [
                            listPageState.activeFilters && listPageState.activeFilters.length,
                            listPageState.currentPageNumber,
                            listPageState.sortingCritera &&
                            listPageState.sortingCritera.Columns &&
                            listPageState?.sortingCritera.Columns.length
                        ];
                    },
                    () => {
                        const input = new GetFullProductsByCollectionInput(
                            listPageState.pageType,
                            this.props.context.request.apiSettings,
                            {
                                Paging: {
                                    Top: this.props.config.itemsPerPage,
                                    Skip: Math.max(listPageState.pageSize * (listPageState.currentPageNumber || 0), 0)
                                },
                                count: true,
                                Sorting: listPageState.sortingCritera || {}
                            },
                            listPageState.activeFilters || [],

                            +(this.props.context.request.urlTokens.itemId || 0),
                            this.props.context.request.query && this.props.context.request.query.q
                                ? this.props.context.request.query.q
                                : this.props.context.request.query?.productId,
                            this.props &&
                                this.props.config &&
                                this.props.config.includeAttributes !== undefined &&
                                this.props.config.includeAttributes === true
                                ? true
                                : false,
                            this.props.context.request.locale,
                            this.props.context.request.url.requestUrl.pathname
                        );
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        getCollectionProducts(input, this.props.context.actionContext).then(productResults => {
                            listPageState.activeProducts = productResults.products;
                            listPageState.totalProductCount = productResults.count;
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            this._getActivePrice();
                            if (
                                this.props.config.enableAffiliationBasedPricing &&
                                this.props.context.actionContext.requestContext?.user?.isAuthenticated
                            ) {
                                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                this._getActivePrice();
                            }
                        });
                    }
                );
            });
        });
        if (this.props.config.enableAffiliationBasedPricing && this.props.context.actionContext.requestContext?.user?.isAuthenticated) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._getActivePrice();
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.props.data.listPageState.then(listPageState => {
            if (listPageState.pageType === 'Search' && this.props.data.products.result?.count === 0) {
                this.props.context.actionContext.update(new SrpBreadcrumbPageInput(), { breadcrumbCheck: true });
            }
        });
        // VSI Customization - START - 22023
        const validRefiners =
            this.props.data.refiners.result &&
            this.props.data.refiners.result.filter(refiner => {
                return refiner.Values.length > 0;
            });
        const sizeRefinerArr: (string | undefined | ProductRefinerValue)[] = [];
        const sizeRefinerObjArr = validRefiners && validRefiners.filter(refiner => refiner?.KeyName?.toLowerCase() === 'size');
        sizeRefinerObjArr && sizeRefinerObjArr?.length > 0
            ? sizeRefinerObjArr[0]?.Values?.map(obj => sizeRefinerArr.push(obj?.LeftValueBoundString?.toLowerCase()))
            : [];

        const configVirtualRefinerNames = getVirtualRefinerNames(this.props.context.request, this.isMattressesCategory);
        const virtualRefinersArr: (string | undefined)[] = [];
        configVirtualRefinerNames &&
            configVirtualRefinerNames.map((refinerStrName: string) => {
                const virtualRefinerObjArr =
                    validRefiners && validRefiners.filter(refiner => refiner?.KeyName?.toLowerCase() === refinerStrName);
                virtualRefinerObjArr?.map(virtualRefiner =>
                    virtualRefiner.Values?.map(obj => virtualRefinersArr.push(obj?.LeftValueBoundString?.toLowerCase()))
                );
            });
        const currentSortedOption = this._getCurrentlySelectedOption();
        const options: SortColumn[] = [this._getSortColumnFromSelectedOption(currentSortedOption || { key: sortOptions.sortByOptionRelevanceDesc, value: this.props.resources.sortByOptionRelevanceDesc })];
        this.setState({
            sortedOptions: options,
            allSizeRefinerValues: sizeRefinerArr,
            allVirtualRefiners: virtualRefinersArr
        });
        // VSI Customization - END - 22023
    }
    public componentDidUpdate(
        prevProps: IMfrmSearchResultContainerProps<IMfrmSearchResultContainerData>,
        prevState: ISearchResultContainerState
    ): void {
        window.onpopstate = () => {
            window.location.reload();
        };
        const menusData =
            this.props.data.navMenuState.result?.menuItemData !== undefined ? this.props.data.navMenuState.result?.menuItemData : undefined;
        if (this.currentNavMenu === undefined || this.currentNavMenu.length <= 0) {
            if (menusData !== undefined) {
                this._filterMenuItem(menusData, this.currentNavMenu);
                const menuItemData = this.currentNavMenu[0];
                if (menuItemData !== undefined) {
                    if (
                        menuItemData.mattressMatcherBannerID !== this.state.lastBannerID &&
                        menuItemData.mattressMatcherBannerID !== undefined &&
                        menuItemData.mattressMatcherBannerID.length > 0
                    ) {
                        this._updateMattressMatcherBannerSlot(menuItemData);
                    }
                    if (menuItemData.mattressMatcherBannerID === undefined || menuItemData.mattressMatcherBannerID.length <= 0) {
                        if (this.state.matcherBannerSlotInd > -1) {
                            this.setState({ matcherBannerSlotInd: -1, lastBannerID: '' });
                        }
                    }
                }
            }
        }
        if (this.state.sortedOptions[0]?.ColumnName !== prevState.sortedOptions[0]?.ColumnName || this.state.sortedOptions[0]?.IsDescending !== prevState.sortedOptions[0]?.IsDescending) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.props.data.listPageState.then(listPageState => {
                // Initialize reaction based on listPageState properties
                const input = new GetFullProductsByCollectionInput(
                    listPageState.pageType,
                    this.props.context.request.apiSettings,
                    {
                        Paging: {
                            Top: this.props.config.itemsPerPage,
                            Skip: Math.max(listPageState.pageSize * (listPageState.currentPageNumber || 0), 0)
                        },
                        count: true,
                        Sorting: listPageState.sortingCritera || {}
                    },
                    listPageState.activeFilters || [],

                    +(this.props.context.request.urlTokens.itemId || 0),
                    this.props.context.request.query && this.props.context.request.query.q
                        ? this.props.context.request.query.q
                        : this.props.context.request.query?.productId,
                    this.props &&
                        this.props.config &&
                        this.props.config.includeAttributes !== undefined &&
                        this.props.config.includeAttributes === true
                        ? true
                        : false,
                    this.props.context.request.locale,
                    this.props.context.request.url.requestUrl.pathname
                );
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                getCollectionProducts(input, this.props.context.actionContext).then(productResults => {
                    listPageState.activeProducts = productResults.products;
                    listPageState.totalProductCount = productResults.count;
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._getActivePrice();
                    if (
                        this.props.config.enableAffiliationBasedPricing &&
                        this.props.context.actionContext.requestContext?.user?.isAuthenticated
                    ) {
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        this._getActivePrice();
                    }
                });
            });
        }
    }

    // tslint:disable-next-line: cyclomatic-complexity
    public render(): JSX.Element {
        const {
            config: {
                imageSettings,
                className
            },
            data: {
                products: productCollection,
                listPageState
            },
            resources
        } = this.props;
        const seoDataEntry = this.props.data.seoData?.result;
        const activeProducts = listPageState.result?.activeProducts;
        const products = !MsDyn365.isBrowser && !ArrayExtensions.hasElements(activeProducts) ? productCollection.result?.products ?? [] : activeProducts ?? [];
        const searchProduct = (this.props.data.listPageState.result && this.props.data.listPageState.result.featureProduct) || null;
        let errorText = '';
        if (!ArrayExtensions.hasElements(products)) {
            errorText = this._pageType === 'Category' ? resources.resultCategoryNotFoundText : resources.resultSearchNotFoundText;
        }

        const featureComponent = (
            <FeatureSearchResultItem
                product={searchProduct}
                context={this.props.context}
                imageSettings={imageSettings}
                resources={resources}
                moduleType={this.props.typeName}
                moduleId={this.props.id}
                telemetryContent={this.telemetryContent}
            />
        );
        const selectedFiltersCount = this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters?.length;
        const categoryNameFromSlug = (this.props?.data?.category?.result?.Name && this.props?.data?.category?.result?.Name) || '';
        const isCategoryPage: boolean = this._getIsCategoryPage();
        const activeRefiners = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters) || [];
        const searchResultContainerViewProps = {
            ...this.props,
            TitleViewProps: this._getCollectionTitle(),
            categoryHierarchy: !this.props.config.disableHierarchy && this._getCategoryHierarchy(),
            refineMenu: this._getRefineMenu(),
            className: classnames('ms-search-result-container', className),
            SearchResultContainer: {
                moduleProps: this.props,
                className: classnames('ms-search-result-container', className)
            },
            sortByOptions: this.props.data.listPageState.result ? this._getSortingDropDown() : null,
            pagination: this._getPagination(),
            ProductsContainer: { className: 'ms-search-result-container__Products' },
            ProductSectionContainer: { className: 'ms-search-result-container__product-section' },
            CategoryNavContainer: { className: 'ms-search-result-container__category-nav-section' },
            RefineAndProductSectionContainer: { className: 'ms-search-result-container__refine-product-section' },
            choiceSummary: this._getChoiceSummary(),
            clearAllBtn: this._getChoiceSummary(true, true),
            modalToggle: this.props.data.listPageState.result ? (
                <ModalToggle
                    text={`${resources.modalTitle} ${selectedFiltersCount! > 0 ? `(${selectedFiltersCount})` : ''}`}
                    ariaLabel={resources.modalTitle}
                    innerRef={this._modalToggleRef}
                    onClick={this._toggleModal}
                    id='search-result-modal'
                />
            ) : null,
            searchResultModal: this._getSearchResultModal(),
            isMobile: this.isMobile,
            errorMessage: errorText && <ErrorMessage text={errorText} />,
            showCategoryBanner: this.state.showCategoryBanner,
            categoryBannerInd: this.state.categoryBannerIndex,
            matcherBannerSlotInd: this.state.matcherBannerSlotInd,
            FeatureSearchContainer: { className: 'ms-feature-search-result' },
            similarLookProduct: searchProduct ? featureComponent : null,
            productPrices: this.productPrices,
            getScrolledProducts: this._getProductInfo,
            addedProducts: this.state.addedProducts,
            onlyNewProducts: this.state.onlyNewProducts,
            refinerRedirect: this.state.isRefinerRedirectEnabled,
            isCategoryPage: isCategoryPage,
            categoryNameFromSlug: categoryNameFromSlug,
            seoDataEntry: seoDataEntry,
            activeRefiners: activeRefiners,
            showProductsLoader: this.state.showProductsLoader,
            itemsPerPageInitialState: this.state.itemsPerPageInitialState
        };
        return this.props.renderView(searchResultContainerViewProps) as React.ReactElement;
    }

    private _filterMenuItem = (menuItemList: IMenuItemData[], currentNavigation: IMenuItemData[]) => {
        if (menuItemList.length > 0) {
            menuItemList.map((item: IMenuItemData) => {
                const foundCurrentNavMenu = this._updateMattressMatcherBannerWithUrlOnly(item);

                if (foundCurrentNavMenu && item.mattressMatcherBannerID !== '') {
                    this.currentNavMenu.push(item);
                    return item;
                } else if (item.subMenu) {
                    this._filterMenuItem(item.subMenu, currentNavigation);
                }
                return;
            });
        }
    };

    private _updateMattressMatcherBannerWithUrlOnly = (navMenuItem: IMenuItemData) => {
        if (navMenuItem === undefined) {
            return false;
        }
        const destinationURL = navMenuItem.clpLink?.destinationUrl !== undefined ? navMenuItem.clpLink?.destinationUrl : '';
        const linkURL = navMenuItem.linkURL !== undefined ? navMenuItem.linkURL : '';

        if ((destinationURL === undefined || destinationURL === '') && (linkURL === undefined || linkURL === '')) {
            return false;
        }
        let toCheckURL = '';
        if (linkURL !== '' && destinationURL !== '') {
            toCheckURL = destinationURL !== undefined ? destinationURL : '';
        } else if (linkURL !== '' && destinationURL === '') {
            toCheckURL = linkURL !== undefined ? linkURL : '';
        } else if (linkURL === '' && destinationURL !== '') {
            toCheckURL = destinationURL !== undefined ? destinationURL : '';
        } else {
            return false;
        }
        return this._parseLink(toCheckURL);
    };

    private _parseLink = (toCheckURL: string): boolean => {
        if (toCheckURL === '/') {
            return false;
        }

        if (toCheckURL !== '' && toCheckURL.includes(this.props.context.actionContext.requestContext.url.requestUrl.pathname)) {
            return true;
        }

        return false;
    };
    private _updateMattressMatcherBannerSlot = (navMenuItem: IMenuItemData) => {
        if (navMenuItem !== undefined) {
            if (this.props.slots.mattressMatcherBanner && this.props.slots.mattressMatcherBanner.length > 0) {
                for (let i = 0; i < this.props.slots.mattressMatcherBanner.length; i++) {
                    const moduleIds = get(this.props.slots.mattressMatcherBanner[i], 'props.config.moduleId') || '';
                    if (moduleIds.length > 0) {
                        const splittedIDs = moduleIds.split(',');
                        for (let k = 0; k < splittedIDs.length; k++) {
                            if (splittedIDs[k] === navMenuItem.mattressMatcherBannerID) {
                                this.setState({ matcherBannerSlotInd: i, lastBannerID: splittedIDs[k] });
                                return true;
                            }
                        }
                    }
                }
            }
        }
        this.setState({ matcherBannerSlotInd: -1, lastBannerID: '' });
        return false;
    };

    private _updateMattressMatcherBanner = (navMenuItem: IMenuItemData) => {
        if (navMenuItem !== undefined) {
            if (this.props.slots.mattressMatcherBanner && this.props.slots.mattressMatcherBanner.length > 0) {
                for (let i = 0; i < this.props.slots.mattressMatcherBanner.length; i++) {
                    const bannerIDs = get(this.props.slots.mattressMatcherBanner[i], 'props.config.bannerID') || '';
                    const moduleIds = get(this.props.slots.mattressMatcherBanner[i], 'props.config.moduleId') || '';
                    if (bannerIDs.length > 0) {
                        const splittedIDs = bannerIDs.split(',');
                        for (let k = 0; k < splittedIDs.length; k++) {
                            if (splittedIDs[k] === navMenuItem.mattressMatcherBannerID) {
                                this.setState({ matcherBannerSlotInd: i, lastBannerID: splittedIDs[k] });
                                return true;
                            }
                        }
                    }
                    if (moduleIds.length > 0) {
                        const splittedIDs = moduleIds.split(',');
                        for (let k = 0; k < splittedIDs.length; k++) {
                            if (splittedIDs[k] === navMenuItem.mattressMatcherBannerID) {
                                this.setState({ matcherBannerSlotInd: i, lastBannerID: splittedIDs[k] });
                                return true;
                            }
                        }
                    }
                }
            }
        }
        return false;
    };

    private _getCategoryBanner = () => {
        let flag = false;
        if (this.props.data.navMenuState.result && this.props.slots.categoryBanner[0]) {
            const navMenuSelectedMenu = this.props.data.navMenuState.result.selectedNavMenu;
            this.props.slots.categoryBanner &&
                this.props.slots.categoryBanner.map((item, index) => {
                    const moduleId = get(item, 'props.config.moduleId') || '';
                    if (moduleId === navMenuSelectedMenu) {
                        if (this.state.categoryBannerIndex === 0 && index === 0 && !this.state.showCategoryBanner) {
                            this.setState({
                                showCategoryBanner: true
                            });
                            flag = true;
                        }
                        if (this.state.categoryBannerIndex !== index) {
                            this.setState({
                                showCategoryBanner: true,
                                categoryBannerIndex: index
                            });
                            flag = true;
                        }
                        return;
                    }
                });
        }
        if (!flag && this.state.showCategoryBanner && this.state.categoryBannerIndex !== 0) {
            this.setState({
                showCategoryBanner: false
            });
        }
    };

    private _getActivePrice = async (): Promise<void> => {
        let products = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeProducts) || [];
        const productRecIds: number[] = [];

        products.length ? products.map(prod => {
            if (prod.IsMasterProduct) {
                const lowestVariantId = prod?.AttributeValues?.find(attributeValue => attributeValue.Name === "EcomLowestPriceVariantRecId")?.TextValue;
                productRecIds.push(isNaN(Number(lowestVariantId)) ? Number(prod?.MasterProductId) : Number(lowestVariantId));
            } else {
                productRecIds.push(Number(prod?.RecordId));
            }
        }) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        const affliations: AffiliationLoyaltyTier[] = [];
        const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
        const customer = await getCustomer(customerInput, this.props.context.actionContext);

        if (customer && customer.CustomerAffiliations) {
            customer.CustomerAffiliations.forEach((affliation: CustomerAffiliation) => {
                const aff: AffiliationLoyaltyTier = { AffiliationId: affliation.RetailAffiliationId, CustomerId: customer.AccountNumber };
                affliations.push(aff);
            });
        }

        await getActivePricesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            affliations,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                this.productPrices = activePricesResponse;
                products = products.map(prod => {
                    let prodVariantId: number;
                    if (prod.IsMasterProduct) {
                        prodVariantId = Number(prod?.AttributeValues?.find(attributeValue => attributeValue.Name === "EcomLowestPriceVariantRecId")?.TextValue);
                    } else {
                        prodVariantId = Number(prod?.RecordId);
                    }
                    const activePrice = activePricesResponse.find(price => price.ProductId === prodVariantId);
                    if (activePrice) {
                        prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
                        prod.BasePrice = Math.max(
                            activePrice.BasePrice || 0,
                            activePrice.TradeAgreementPrice || 0,
                            activePrice.AdjustedPrice || 0
                        );
                    }
                    return prod;
                });
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this.props.data.listPageState.then(listPageState => {
                    listPageState.activeProducts = products;
                });
            })
            .catch((error: Error) => {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    `[get Affiliation based price on search result container ]Error executing action`
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
    };
    private _getActivePriceByProducts = async (products: ProductSearchResult[]): Promise<ProductSearchResult[]> => {
        const productRecIds: number[] = [];
        products.length ? products.map(prod => {
            if (prod.IsMasterProduct) {
                const lowestVariantId = prod?.AttributeValues?.find(attributeValue => attributeValue.Name === "EcomLowestPriceVariantRecId")?.TextValue;
                productRecIds.push(isNaN(Number(lowestVariantId)) ? Number(prod?.MasterProductId) : Number(lowestVariantId));
            } else {
                productRecIds.push(Number(prod?.RecordId));
            }
        }) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        const affliations: AffiliationLoyaltyTier[] = [];
        const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
        const customer = await getCustomer(customerInput, this.props.context.actionContext);

        if (customer && customer.CustomerAffiliations) {
            customer.CustomerAffiliations.forEach((affliation: CustomerAffiliation) => {
                const aff: AffiliationLoyaltyTier = { AffiliationId: affliation.RetailAffiliationId, CustomerId: customer.AccountNumber };
                affliations.push(aff);
            });
        }

        return getActivePricesAsync(
            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            affliations,
            true
        )
            .then((activePricesResponse: ProductPrice[]) => {
                this.productPrices = activePricesResponse;
                return products.map(prod => {
                    const variantId = prod.IsMasterProduct ? Number(prod.AttributeValues?.find(attributeValue => attributeValue.Name === "EcomLowestPriceVariantRecId")?.TextValue) : prod.RecordId;
                    const activePrice = activePricesResponse.find(price => price.ProductId === variantId);
                    if (activePrice) {
                        prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
                        prod.BasePrice = Math.max(
                            activePrice.BasePrice || 0,
                            activePrice.TradeAgreementPrice || 0,
                            activePrice.AdjustedPrice || 0
                        );
                    }
                    return prod;
                });
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                //return products;
            })
            .catch((error: Error) => {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    `[get Affiliation based price on search result container ]Error executing action`
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
    };
    private _getSearchResultModal = (): ISearchResultModalViewProps => {
        const { resources } = this.props;
        return SearchResultModal({
            resources: {
                modalCloseButtonText: resources.modalCloseButtonText,
                modalTitle: resources.modalTitle
            },
            isOpen: this.state.modalIsOpen,
            totalItems: this.props.data && this.props.data.listPageState && this.props.data.listPageState.result?.totalProductCount,
            returnRef: this._modalToggleRef,
            clearAllBtn: this._getChoiceSummary(true),
            onModalToggle: this._toggleModal
        });
    };

    private _getCollectionTitle = (): ITitleViewProps => {
        const { data, context, resources } = this.props;
        const seoDataEntry = this.props.data.seoData?.result;
        const seoState = seoDataEntry && seoDataEntry['SEO State'];
        const seoTitle = seoState === 'Active' && seoDataEntry && seoDataEntry['H1 Tag'];
        const pageName: string | undefined = context.request.telemetryPageName;
        let collectionTitle: string | undefined = '';
        if (context && context.request && context.request.query && context.request.query.q && pageName === 'Search') {
            collectionTitle = `${context.request.query.q}`;
        } else {
            collectionTitle =
                seoTitle || (data.category.result &&
                    SearchResultContainer.getFriendlyName(context.request.locale, data.category.result.NameTranslations)) ||
                (data.category.result && data.category.result.Name);
        }
        let productCountText = '';
        let productCountNumber: number | undefined;
        if (data.listPageState && data.listPageState.result && data.listPageState.result.totalProductCount !== undefined) {
            productCountNumber = data.listPageState.result.totalProductCount;
        } else if (data.products && data.products.result) {
            productCountNumber = data.products.result.count;
        }

        if (productCountNumber && productCountNumber !== 0) {
            productCountText =
                productCountNumber !== 1
                    ? format(`${this.props.resources.numberOfProducts} results`, productCountNumber)
                    : this.props.resources.oneProduct;
        } else {
            productCountText = format(`${this.props.resources.numberOfProducts} results`, 0);
        }
        const searchPageTitle = <h1 className='ms-search-result__search-page-title'>{resources.searchPageTitle}</h1>;
        const titlePrefix = <Title className='ms-search-result__collection-title-prefix' text={resources.searchTextPrefix} />;
        const titleText = collectionTitle && <h1 className='ms-search-result__collection-title-text'>{collectionTitle}</h1>;
        const titleCount = <Title className='ms-search-result__collection-title-count' text={productCountText} />;

        return {
            TitleContainer: { className: 'ms-search-result-container__title' },
            title: {
                searchPageTitle: searchPageTitle,
                titlePrefix: titlePrefix,
                titleText: titleText,
                titleCount: titleCount
            }
        };
    };

    private _getCategoryHierarchy = (): ICategoryHierarchyViewProps => {
        const { data } = this.props;
        const categoryHierarchy = data.categoryHierarchy.result;
        const categoryLinks =
            categoryHierarchy &&
            categoryHierarchy.map((value: CategoryHierarchyData, index: number) => {
                return (
                    <Link
                        key={index}
                        text={value.Name}
                        ariaLabel={`${this.props.resources.categoryLinkAriaLabel} ${value.Name}`}
                        href={value.Url}
                    />
                );
            });
        const categoryLinkSeparator = <Separator separator='/' />;

        return {
            CategoryHierarchyContainer: { tag: 'nav', className: 'ms-search-result-container__category-hierarchy' },
            categoryHierarchyList: categoryLinks,
            categoryHierarchySeparator: categoryLinkSeparator
        };
    };

    private _getSortingDropDown = (): ISortByViewProps => {
        const { resources } = this.props;
        const activeDropdown = this._getCurrentlySelectedOption() || this.state.sortingState.selectedSortByOption;
        const dropdown = (
            <UiLabeledDropdown
                labelClassname='reviews-list-sort-by'
                labelText={resources.sortByDropdownLabel}
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={this.sortByDropdownOptions}
                selectedOption={activeDropdown}
                onSelectOption={this._updateSortByDropdown}
                ref={this._sortAndFilterContainerRef}
            />
        );
        return {
            sortByDropDown: dropdown
        };
    };

    private _getProductInfo = async () => {
        const { data } = this.props;
        const listPageState = data && data.listPageState && data.listPageState.result;

        // @ts-ignore
        const input = new GetFullProductsByCollectionInput(
            listPageState!.pageType,
            this.props.context.request.apiSettings,
            {
                Paging: {
                    Top: this.props.config.itemsPerPage,
                    Skip: Math.max(listPageState!.pageSize * (this.state.infiniteScrollPage || 0), 0)
                },
                count: true,
                Sorting: listPageState?.sortingCritera || {}
            },
            listPageState?.activeFilters || [],
            +(this.props.context.request.urlTokens.itemId || 0),
            this.props.context.request.query && this.props.context.request.query.q
                ? this.props.context.request.query.q
                : this.props.context.request.query?.productId,
            this.props &&
                this.props.config &&
                this.props.config.includeAttributes !== undefined &&
                this.props.config.includeAttributes === true
                ? true
                : false,
            this.props.context.request.locale
        );

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getProducts(input, this.props.context.actionContext).then(actionResponse => {
            if (actionResponse.products.length) {
                const currentProducts = (data.listPageState.result && data.listPageState.result.activeProducts) || [];
                const returnedProducts = actionResponse.products.filter(product => typeof product === 'object');
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._getActivePriceByProducts(returnedProducts);
                const infiniteProducts = this.state.addedProducts.length
                    ? this.state.addedProducts.concat(returnedProducts)
                    : currentProducts.concat(returnedProducts);
                this.setState({ addedProducts: infiniteProducts, onlyNewProducts: returnedProducts });
                this.setState({ infiniteScrollPage: this.state.infiniteScrollPage + 1 });
            }
        });
    };

    private _getPagination = (): React.ReactNode => {
        const { config, context, data, resources } = this.props;
        const listPageState = data && data.listPageState && data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || 10;
        const skipCount =
            listPageState && listPageState.currentPageNumber !== null
                ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || 10)
                : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = (listPageState && listPageState.totalProductCount) || 0;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp={'skip'}
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={
                    <div className='msc-pagination__prev'>
                        <span className='ms-search-result__pagination-left' aria-hidden='true' />
                        <span className='prev-text'>{previousText}</span>
                    </div>
                }
                nextText={
                    <div className='msc-pagination__next'>
                        <span className='next-text'>{nextText}</span>
                        <span className='ms-search-result__pagination-right' aria-hidden='true' />
                    </div>
                }
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                telemetryContent={this.telemetryContent}
            />
        );
    };

    // tslint:disable: max-func-body-length cyclomatic-complexity
    private _getRefineMenu = (): IRefineMenuViewProps => {
        const { data, context, config, resources } = this.props;
        const tempRangeTypeTODO = context.request.query && context.request.query.inputRange ? 'input' : 'slider';
        const validRefiners =
            data.refiners.result &&
            data.refiners.result.filter(refiner => {
                return refiner.Values.length > 0;
            });
        let sizeRefinerArr: (string | undefined)[] = [];
        validRefiners &&
            validRefiners.map(refiner => {
                if (refiner && refiner?.KeyName?.toLowerCase() === 'size') {
                    sizeRefinerArr = refiner?.Values?.map(obj => obj?.LeftValueBoundString?.toLowerCase());
                }
            });
        let isPriceRefinerHide = false;
        const isAnySizeSelectedArr = this.props?.data?.listPageState?.result?.activeFilters?.filter(obj =>
            sizeRefinerArr.includes(obj?.LeftValueBoundString?.toLowerCase())
        );
        if (isAnySizeSelectedArr && isAnySizeSelectedArr?.length > 0) {
            isPriceRefinerHide = true;
        }
        const activeRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        const defaultOpenRefinersArray: string[] = [];
        const selectedRefinersParentName: string[] = [];

        activeRefiners &&
            activeRefiners.length > 0 &&
            activeRefiners.map((selectedRefiner: ProductRefinerValue) => {
                const obj = {
                    key: this._getKeyForRefinerValue(selectedRefiner),
                    parentName: this._getParentKeyForRefinerValue(selectedRefiner)
                };
                selectedRefinersParentName.push(obj.parentName.trim().toLowerCase());
            });
        config.defaultOpenRefinements?.map(refiner => {
            // @ts-ignore
            defaultOpenRefinersArray.push(refiner.refiner.trim().toLowerCase());
        });
        const determineExpanded = (product: IProductRefinerHierarchy) => {
            const refinerNamesForInitialOpen = selectedRefinersParentName.concat(...defaultOpenRefinersArray);
            return product.KeyName && refinerNamesForInitialOpen?.includes(product.KeyName.trim().toLowerCase()) ? true : false;
        };

        // VSI Customization - START - 22023
        // get attribute value to show or hide Price refinement
        const isSizeRefinementBooleanValue = this.isMattressesCategory ? true : false;
        // VSI Customization - START - 23629
        const refinementExcludeListString = this.props.config.refinementExcludeList;
        const refinementExcludeListArr = refinementExcludeListString?.split(',');
        const refinementExcludeListNorm = refinementExcludeListArr?.map(refinerName => refinerName.trim().toLowerCase());
        // VSI Customization - END - 23629

        // VSI Customization - Start 7924
        const requestContext = context && context.request;
        // get category slug
        let slug = (this.props.data.category.result && this.props.data.category.result.Slug) || '';
        let slugArray: string[];
        let pathArray: string[];
        let refinerParts: string[] = [];
        let urlPartsObject: IRefineItemURLParts;
        const isSizeRefinement = this.props?.data.category.result?.ExtensionProperties?.find(attr => attr.Key === 'SizeRefinementEnable')
            ?.Value?.BooleanValue;
        const configVirtualRefinerNames = getVirtualRefinerNames(this.props.context.request, isSizeRefinement);
        const filteredRefiners =
            this.props.data.refiners.result &&
            this.props.data.refiners.result.filter(refiner => {
                return refiner.KeyName && configVirtualRefinerNames.indexOf(refiner.KeyName.toLowerCase()) !== -1;
            });
        const sortedConfigValues = getSortedVirtualRefinerConfig(this.props.context.request, this.isMattressesCategory);
        if (sortedConfigValues && slug) {
            const params = MsDyn365.isBrowser && window.location;
            const origin = params && params.origin === 'https://localhost:4000' ? params.origin : requestContext.url.requestUrl.origin;
            slugArray = slug.split('/').splice(1);
            // remove keywords form slugArray
            slugArray = removeKeywords(slugArray, this.props.context.request);
            // FIX reset slug to without keywords
            slug = slugArray.join('/');

            // path of URL
            let path = this.props.context.request.url.requestUrl.pathname.toLowerCase();

            // remove category id {categoryid}.c
            const categoryIdToken = requestContext.urlTokens.recordId;
            const categoryId = categoryIdToken ? `${categoryIdToken}.c` : undefined;

            if (categoryId) {
                path = path?.replace(`/${categoryId}`, '');
            }
            // remove category slug
            // FIX remove keywords from path
            pathArray = removeKeywords(path.split('/').splice(1), this.props.context.request);
            path = pathArray.join('/');
            const removedCategorySlug = path.split(slug).splice(1)[0];

            if (removedCategorySlug !== '') {
                refinerParts = decodeURIComponent(removedCategorySlug).split('/');
            }

            if (refinerParts && refinerParts.length > 0 && slugArray && slugArray.length > 0) {
                // remove duplicates if any from the first url part
                slugArray.forEach(_slug => {
                    refinerParts.forEach(refinerPart => {
                        const index = slugArray.indexOf(refinerPart);
                        if (index !== -1) {
                            slugArray.splice(index, 1);
                        }
                    });
                });
            }

            // break final refiners so array contains single values instead of pipe sign values
            const finalRefinerParts: string[] = [];
            refinerParts.forEach(refinerPart => {
                refinerPart.split('|').map(item => finalRefinerParts.push(item));
            });

            refinerParts = finalRefinerParts && flatten(finalRefinerParts);
            refinerParts = refinerParts.filter(refiner => {
                return refiner.length > 0;
            });

            urlPartsObject = {
                origin,
                categoryPart: slugArray.join('/'),
                categoryId
            };
        }
        // VSI Customization - End 7924
        const subMenus =
            validRefiners &&
            validRefiners.map((productRefinerHierarchy: IProductRefinerHierarchy, index: number) => {
                // VSI Customization - START - 23629
                const excludedElement = refinementExcludeListNorm?.find(
                    refinerName => productRefinerHierarchy?.KeyName?.trim().toLowerCase() === refinerName
                );
                if (excludedElement) {
                    return undefined;
                }
                // VSI Customization - END - 23629

                return (
                    <ErrorBoundary telemetry={context.telemetry} key={index}>
                        <RefineSubmenu
                            productRefinerHierarchy={productRefinerHierarchy}
                            selectedRefinerValues={activeRefiners}
                            refineItemCommonProps={this._refineItemCommonProps}
                            minValueSliderThumbAriaLabel={this.props.resources.minValueSliderThumbAriaLabel}
                            maxValueSliderThumbAriaLabel={this.props.resources.maxValueSliderThumbAriaLabel}
                            refinerShowMoreText={this.props.resources.refinerShowMoreText}
                            refinerShowLessText={this.props.resources.refinerShowLessText}
                            key={index}
                            onUpdateRefiners={this._onUpdateRefiners}
                            urlBuilder={this._buildRefinerUrl}
                            isDisabled={false}
                            isExpandedOnInitialLoad={determineExpanded(productRefinerHierarchy)}
                            tempRangeTypeTODO={tempRangeTypeTODO}
                            context={context}
                            moduleId={this.props.id}
                            moduleTypeName={this.props.typeName}
                            telemetryContent={this.telemetryContent}
                            refinerNumberOfItemsToDisplay={this._getRefinerLimitToDisplay}
                            isMattressesCategory={this.isMattressesCategory}
                            urlRefinerParts={refinerParts}
                            urlParts={urlPartsObject}
                            sortedConfigValues={sortedConfigValues}
                            filteredRefiners={filteredRefiners}
                            isPriceRefinerDisabled={isPriceRefinerHide && isSizeRefinementBooleanValue}
                            isCategoryPage={this._getIsCategoryPage()}
                            refineItemAriaLabel={this.props.resources.refineItemAriaLabel}
                            refineItemsAriaLabel={this.props.resources.refineItemsAriaLabel}
                            singleProductCountAriaLabel={this.props.resources.singleProductCountAriaLabel}
                            isMobile={this.isMobile}
                        />
                    </ErrorBoundary>
                );
            });

        return {
            RefineMenuContainer: { className: 'ms-search-result-container__refine-menu' },
            RefinerSectionContainer: { className: 'ms-search-result-container__refiner-section' },
            refiners: subMenus,
            refinerLabel: resources.mfrmRefinerLabel
        };
    };
    private _getRefinerLimitToDisplay = (keyName: string): number | undefined => {
        // this function will filter name of refiner to get its manual added value to limit No of items
        return this.props.config.defaultOpenRefinements?.filter(refiner =>
            keyName === 'Size' ? refiner.refinerNumberOfItemsToDisplay : undefined
        )[0]?.refinerNumberOfItemsToDisplay;
    };
    private _getChoiceSummary = (clearAllModal: boolean = false, clearAllBtn: boolean = false): React.ReactNode => {
        const { resources, data, telemetry } = this.props;
        const selectedRefiners = (data.listPageState.result && data.listPageState.result.activeFilters) || [];
        return (
            <ErrorBoundary telemetry={telemetry}>
                <ChoiceSummary
                    classNames='ms-choice-summary-by-category'
                    clearAllText={resources.clearAllText}
                    label={resources.choiceSummaryLabel}
                    selectedChoices={selectedRefiners}
                    clearAllModal={clearAllModal}
                    // @ts-ignore: NOTE Type-unsafe line below, null refinersByCategoryHierarchy case not handled
                    refinerHierarchy={data.refiners.result}
                    choiceFormat={resources.choiceFormat}
                    choiceRangeValueFormat={resources.choiceRangeValueFormat}
                    telemetry={telemetry}
                    onChoiceClicked={this._onChoiceClicked}
                    urlBuilder={this._buildRefinerUrlForChoiceSummary}
                    customUrlBuilder={this._customBuildRefinerUrlForChoiceSummary}
                    choiceAriaLabel={resources.choiceAriaLabel}
                    telemetryContent={this.telemetryContent}
                    clearAllBtn={clearAllBtn}
                    closeAriaLabel={resources.closeAriaLabel}
                />
            </ErrorBoundary>
        );
    };
    private _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        const {
            context: {
                request: requestContext,
                actionContext
            }
        } = this.props;
        // @ts-ignore
        if (MsDyn365.isBrowser && utag) {
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'PLP-sort-action',
                event_category: 'PLP Sort Action',
                event_action: 'PLP Sort Applied',
                event_label: `${notification.selectedOption.value}`,
                event_noninteraction: 'false'
            });
        }
        this.setState({ addedProducts: [], onlyNewProducts: [], infiniteScrollPage: 1 });
        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `[sort-by-category] cannot sort without context: ${!requestContext ? 'requestContext ' : ''} ${!actionContext ? 'actionContext ' : ''
                } could not be found`;
            this.props.telemetry.warning(error);
            return;
        }
        MsDyn365.isBrowser &&
            window.history.pushState(
                {},
                '',
                buildListPageUrl(
                    getCurrentUrl(this.props.context.request),
                    undefined,
                    [this._getSortColumnFromSelectedOption(notification.selectedOption)],
                    undefined
                )
            );
        const options: SortColumn[] = [this._getSortColumnFromSelectedOption(notification.selectedOption)];
        this.setState({
            sortedOptions: options
        });
        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.sortingCritera = {
                Columns: [this._getSortColumnFromSelectedOption(notification.selectedOption)]
            };
        });
    };

    private _getSortColumnFromSelectedOption = (option: ILabeledDropdownOption): SortColumn => {
        const { data } = this.props;
        if (!data.searchConfiguration.result) {
            return {};
        }
        const mappedConfiguration = data.searchConfiguration.result.find(searchConfiguration => {
            return searchConfiguration.key === option.key;
        });
        if (mappedConfiguration) {
            return mappedConfiguration.sortColumn;
        }
        return {};
    };
    private _getCurrentlySelectedOption = (): ILabeledDropdownOption | undefined => {
        const { data } = this.props;
        const sortCriteria = data.listPageState.result && data.listPageState.result.sortingCritera;

        const activeSortColumn =
            (sortCriteria && sortCriteria.Columns && sortCriteria.Columns[0]) ||
            (parseQueryParam<SortColumn[]>('sorting', this.props.context.request) || [])[0];
        if (activeSortColumn && data.searchConfiguration.result) {
            const activeMappedConfig = data.searchConfiguration.result.find(mappedSearchConfig => {
                return (
                    mappedSearchConfig.sortColumn.ColumnName === activeSortColumn.ColumnName &&
                    mappedSearchConfig.sortColumn.IsDescending === activeSortColumn.IsDescending
                );
            });
            if (activeMappedConfig) {
                return this.sortByDropdownOptions.find(dropdownOption => dropdownOption.key === activeMappedConfig.key);
            }
        }
        return;
    };

    // tslint:disable: cyclomatic-complexity
    // tslint:disable: max-func-body-length
    private _onUpdateRefiners = (itemToggleNotification: IRefineItemToggleNotification): void => {
        const {
            context: {
                request: requestContext,
                actionContext
            }
        } = this.props;
        const isCategoryPage: boolean = this._getIsCategoryPage();
        if (this._isCustomizedRefiner && isCategoryPage) {
            this.setState({ isRefinerRedirectEnabled: true });
        }
        // @ts-ignore
        if (!this.isMobile && utag) {
            const { LeftValueBoundLocalizedString, LeftValueBoundString } = itemToggleNotification?.productRefinerValue;
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'PLP-filter-action',
                event_category: 'PLP Filter Action',
                event_action: itemToggleNotification?.parentProductRefinerHierarchy.KeyName,
                event_label: LeftValueBoundLocalizedString ? LeftValueBoundLocalizedString : LeftValueBoundString,
                event_noninteraction: 'false'
            });
        }
        if (!requestContext || !actionContext || !this.props.data.listPageState.result) {
            const error = `Refine menu cannot refine search criteria: ${!requestContext ? 'requestContext ' : ''} ${!actionContext ? 'actionContext ' : ''
                } could not be found`;
            this.props.telemetry.warning(error);
            return;
        }
        this.setState({ addedProducts: [], onlyNewProducts: [], infiniteScrollPage: 1 });
        let currentRefinement = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters) || [];
        if (this._isCustomizedRefiner && isCategoryPage) {
            currentRefinement = (this.props.data.listPageState.result && this.props.data.listPageState.result.activeVirtualFilters) || [];
        }
        let updatedRefinementCriteria = getUpdatedRefinementCriteria(itemToggleNotification, currentRefinement);
        // VSI Customization - START - 22023
        if (isCategoryPage) {
            const isAnySizeSelectedArr = updatedRefinementCriteria.filter(obj =>
                this.state.allSizeRefinerValues.includes(obj?.LeftValueBoundString?.toLowerCase())
            );
            if (isAnySizeSelectedArr && isAnySizeSelectedArr?.length > 0) {
                this.setState({ isPriceRefinerDisabled: true });
                updatedRefinementCriteria = this._removePriceAndVirtualRefiner(updatedRefinementCriteria);
            } else {
                this.setState({ isPriceRefinerDisabled: false });
            }
        }
        // VSI Customization - END - 22023
        // PLP-URL
        const isSizeRefinement = this.props?.data.category.result?.ExtensionProperties?.find(attr => attr.Key === 'SizeRefinementEnable')
            ?.Value?.BooleanValue;
        const availableRefiners = getVirtualRefinerNames(this.props.context.request, isSizeRefinement);
        if (!isCategoryPage) {
            window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        } else if (itemToggleNotification.parentProductRefinerHierarchy.KeyName && availableRefiners.includes(itemToggleNotification.parentProductRefinerHierarchy.KeyName.toLowerCase()) === false) {
            this._isCustomizedRefiner ?
                window.location.href = buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria) :
                window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), updatedRefinementCriteria));
        } else {
            // url builder
            const configVirtualRefinerNames = getVirtualRefinerNames(this.props.context.request, isSizeRefinement);
            const filteredRefiners = this.props.data.refiners.result && this.props.data.refiners.result.filter((refiner) => {
                return refiner.KeyName && configVirtualRefinerNames.indexOf(refiner.KeyName.toLowerCase()) !== -1;
            });
            // get category slug
            let slug = (this.props.data.category.result && this.props.data.category.result.Slug) || '';
            let slugArray: string[];
            let pathArray: string[];
            const sortedConfigValues = getSortedVirtualRefinerConfig(this.props.context.request, isSizeRefinement);
            if (sortedConfigValues && slug) {
                const params = MsDyn365.isBrowser && window.location;
                const origin = params && params.origin === 'https://localhost:4000' ? params.origin : requestContext.url.requestUrl.origin;
                slugArray = slug.split('/').splice(1);
                // remove keywords form slugArray
                slugArray = removeKeywords(slugArray, this.props.context.request);
                // FIX reset slug to without keywords
                slug = slugArray.join('/');

                // path of URL
                let path = this.props.context.request.url.requestUrl.pathname.toLowerCase();

                // remove category id {categoryid}.c
                const categoryIdToken = requestContext.urlTokens.recordId;
                const categoryId = categoryIdToken ? `${categoryIdToken}.c` : undefined;

                if (categoryId) {
                    path = path?.replace(`/${categoryId}`, '');
                }
                // remove category slug
                pathArray = removeKeywords(path.split('\/').splice(1), this.props.context.request);
                path = pathArray.join('\/');
                const removedCategorySlug = path.split(slug).splice(1)[0];
                let refinerParts: string[] = [];
                if (removedCategorySlug !== '') {
                    refinerParts = decodeURIComponent(removedCategorySlug).split('/');
                }

                if (refinerParts && refinerParts.length > 0 && slugArray && slugArray.length > 0) {
                    // remove duplicates if any from the first url part
                    slugArray.forEach(_slug => {
                        refinerParts.forEach(refinerPart => {
                            const index = slugArray.indexOf(refinerPart);
                            if (index !== -1) {
                                slugArray.splice(index, 1);
                            }
                        });
                    });
                }
                // insert current refiner
                itemToggleNotification.isSelecting &&
                    refinerParts.push(itemToggleNotification.productRefinerValue.LeftValueBoundString!.toLowerCase());
                // break final refiners so array contains single values instead of pipe sign values
                const finalRefinerParts: string[] = [];
                refinerParts.forEach(refinerPart => {
                    refinerPart.split('|').map(item => finalRefinerParts.push(item.includes('-') ? item : item.split('-').join(' ')));
                });

                refinerParts = finalRefinerParts && flatten(finalRefinerParts);
                if (itemToggleNotification.isSelecting === false) {
                    const index = refinerParts.indexOf(itemToggleNotification.productRefinerValue.LeftValueBoundString!.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-'));
                    if (index !== -1) {
                        refinerParts.splice(index, 1);
                    }
                }

                // setup object with refiner values
                const sortedValues: { [key: string]: string[] } = {};
                filteredRefiners?.forEach(refinerSet => {
                    let refinerName = refinerSet.KeyName;
                    if (refinerName) {
                        refinerName = refinerName.toLowerCase();
                        sortedValues[refinerName] = []; // size, brand, price
                    }
                    refinerSet.Values.forEach((refiner) => {
                        refinerParts.forEach((refinerPart) => {
                            if (refinerName && refiner.LeftValueBoundString &&
                                (refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLocaleLowerCase().split('-').join(' ')
                                    || refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLocaleLowerCase())) {
                                sortedValues[refinerName].push(refiner.LeftValueBoundString.toLowerCase().split(' ').join('-'));
                            } else if (refinerName && refiner.LeftValueBoundString &&
                                refiner.LeftValueBoundString.toLowerCase() === refinerPart.toLocaleLowerCase().split('-').join(' ')) {
                                sortedValues[refinerName].push(refinerPart);
                            }
                        });
                    });
                });
                // alpha sort
                // @ts-ignore
                for (const sortedValue of Object.keys(sortedValues)) {
                    sortedValues[sortedValue].length > 0 &&
                        sortedValues[sortedValue].sort((a: string, b: string) => {
                            if (a < b) {
                                return -1;
                            }
                            if (a > b) {
                                return 1;
                            }
                            return 0;
                        });
                }
                // @ts-ignore
                const finalRefiners: string[] = [];
                sortedConfigValues.forEach(sortedConfigValue => {
                    const sortedRefinerName = sortedConfigValue.refinerName.toLowerCase();
                    if (sortedValues[sortedRefinerName] !== undefined && sortedValues[sortedRefinerName].length > 0) {
                        let concatValue: string | string[] = sortedValues[sortedRefinerName];
                        if (sortedValues[sortedRefinerName].length > 1) {
                            concatValue = sortedValues[sortedRefinerName].join('|');
                        } else if (sortedValues[sortedRefinerName].length === 1) {
                            concatValue = sortedValues[sortedRefinerName][0];
                        }
                        // @ts-ignore
                        finalRefiners.push(concatValue.split(' ').join('-')?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-'));
                    }
                });

                // @ts-ignore
                const finalURL = `${origin}/${slugArray.join('/')}${finalRefiners && finalRefiners.length > 0 ? `/${finalRefiners.join('/')}` : ''
                    }${categoryId ? `/${categoryId}` : ''}${params ? params.search : ''}`; // todo: will remove after complete functionality
                window.location.href = finalURL.trim();
            }
        }

        transaction(() => {
            this.props.data.listPageState.result!.currentPageNumber = 0;
            this.props.data.listPageState.result!.activeFilters = updatedRefinementCriteria;
        });
    };

    private _buildRefinerUrl = (itemToggleNotification: IRefineItemToggleNotification): string => {
        if (this.props.data.listPageState.result) {
            const newRefinementCriteria = getUpdatedRefinementCriteria(
                itemToggleNotification,
                (this._isCustomizedRefiner && this.props.data.listPageState.result
                    ? this.props.data.listPageState.result?.activeVirtualFilters
                    : this.props.data.listPageState.result?.activeFilters) || []
            );

            return buildListPageUrl(getCurrentUrl(this.props.context.request), newRefinementCriteria);
        } else {
            this.props.telemetry.warning(`[buildRefinerQueryString]List Page State Not available, unable to build refiner URL`);
            return '';
        }
    };

    private _onChoiceClicked = (notification: IChoiceSummaryClickNotification): void => {
        this.setState({ addedProducts: [], onlyNewProducts: [], infiniteScrollPage: 1 });
        const { data, telemetry, context } = this.props;
        const requestContext = context && context.request;
        const isCustomizeRefiner = requestContext.app.config.urlModifications && requestContext.app.config.urlModifications.length > 0;
        const isCategoryPage: boolean = this._getIsCategoryPage();
        if (isCustomizeRefiner && isCategoryPage) {
            this.setState({ isRefinerRedirectEnabled: true });
        }

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return;
        }
        let currentRefinementCriteria = this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters || [];
        if (this._isCustomizedRefiner && isCategoryPage) {
            currentRefinementCriteria =
                (this.props.data.listPageState.result && this.props.data.listPageState.result.activeVirtualFilters) || [];
        }

        if (!notification.clearAll) {
            const selectedChoice = notification.choiceClicked;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );
            // VSI Customization - START - 22023

            if (isCategoryPage) {
                const isAnySizeSelectedArr = currentRefinementCriteria?.filter((obj: ProductRefinerValue) =>
                    this.state.allSizeRefinerValues.includes(obj?.LeftValueBoundString?.toLowerCase())
                );
                if (isAnySizeSelectedArr && isAnySizeSelectedArr?.length > 0) {
                    this.setState({ isPriceRefinerDisabled: true });
                    currentRefinementCriteria = this._removePriceAndVirtualRefiner(currentRefinementCriteria);
                } else {
                    this.setState({ isPriceRefinerDisabled: false });
                }
            }

            // VSI Customization - END - 22023
        } else {
            currentRefinementCriteria = [];
            // @ts-ignore
            if (MsDyn365.isBrowser && utag) {
                // @ts-ignore
                // tslint:disable-next-line: object-literal-key-quotes
                utag.link({
                    event: 'PLP-filter-action',
                    event_category: 'PLP Filter Action',
                    event_action: 'Clear All Filters Click',
                    event_label: 'Clear All Filters',
                    event_noninteraction: 'false'
                });
            }
            // set focus to sort and filter
            const dropdownElementId = this._sortAndFilterContainerRef.current && this._sortAndFilterContainerRef.current.props.dropdownId;
            const dropdownElement = MsDyn365.isBrowser && dropdownElementId && document.getElementById(dropdownElementId);
            MsDyn365.isBrowser &&
                setTimeout(() => {
                    dropdownElement && dropdownElement.focus();
                }, 50);
        }
        // VSI Customization - START - 22023
        if (notification.clearAll) {
            this.setState({ isPriceRefinerDisabled: false });
        }
        // VSI Customization - END - 22023
        const virtualRefinersRecordId: number[] = [];
        const isSizeRefinement: boolean | undefined = this.props?.data.category.result?.ExtensionProperties?.find(attr => attr.Key === 'SizeRefinementEnable')?.Value
            ?.BooleanValue;
        const availableRefiners = getVirtualRefinerNames(this.props.context.request, isSizeRefinement);
        this.props.data.refiners.result &&
            this.props.data.refiners.result.filter(refiner => {
                if (availableRefiners.includes(refiner.KeyName?.toLowerCase() as string)) {
                    virtualRefinersRecordId.push(refiner.RecordId!);
                }
            });

        const categoryIdToken = requestContext.urlTokens.recordId;
        const categoryId = categoryIdToken ? `${categoryIdToken}.c` : undefined;
        const fullUrl = getCurrentUrl(this.props.context.request);
        if (!this._isCustomizedRefiner && notification?.choiceClicked?.DataTypeValue === 1) {
            const listPageUrl = buildListPageUrl(fullUrl, currentRefinementCriteria);
            if (!notification.clearAll && virtualRefinersRecordId && virtualRefinersRecordId.length >= 1) {
                // window.location.href = listPageUrl.trim();
            } else {
                MsDyn365.isBrowser && window.history.pushState({}, '', listPageUrl);
            }
        } else if (
            notification.clearAll ||
            (notification.choiceClicked &&
                notification.choiceClicked?.RefinerRecordId &&
                virtualRefinersRecordId.includes(notification.choiceClicked.RefinerRecordId) === false)
        ) {
            const listPageUrl = buildListPageUrl(fullUrl, currentRefinementCriteria);
            if (
                isCustomizeRefiner &&
                isCategoryPage &&
                !notification.clearAll &&
                virtualRefinersRecordId &&
                virtualRefinersRecordId.length >= 1
            ) {
                window.location.href = listPageUrl.trim();
            } else {
                MsDyn365.isBrowser && window.history.pushState({}, '', listPageUrl);
            }
            // below code is to clear virtual refiners if defined in config
            if (
                isCustomizeRefiner &&
                isCategoryPage &&
                notification.clearAll &&
                virtualRefinersRecordId &&
                virtualRefinersRecordId.length >= 1
            ) {
                // get category slug
                let slug = (this.props.data.category.result && this.props.data.category.result.Slug) || '';
                let slugArray: string[];
                const params = MsDyn365.isBrowser && window.location;
                const origin = params && params.origin === 'https://localhost:4000' ? params.origin : requestContext.url.requestUrl.origin;
                slugArray = slug.split('/').splice(1);
                // remove keywords form slugArray
                slugArray = removeKeywords(slugArray, this.props.context.request);
                slug = slugArray.join('/');
                data.listPageState.result.currentPageNumber = 0;
                data.listPageState.result.activeFilters = currentRefinementCriteria;
                const finalURL = `${origin}/${slugArray.join('/')}${categoryId ? `/${categoryId}` : ''}`;
                window.location.href = finalURL.trim();
            }
        } else if (isCustomizeRefiner && isCategoryPage && availableRefiners.includes(notification.parentKeyName.toLowerCase()) === true) {
            // url builder
            const configVirtualRefinerNames = getVirtualRefinerNames(this.props.context.request, isSizeRefinement);
            const filteredRefiners =
                this.props.data.refiners.result &&
                this.props.data.refiners.result.filter(refiner => {
                    return refiner.KeyName && configVirtualRefinerNames.indexOf(refiner.KeyName.toLowerCase()) !== -1;
                });

            // get category slug
            let slug = (this.props.data.category.result && this.props.data.category.result.Slug) || '';
            let slugArray: string[];
            let pathArray: string[];
            const sortedConfigValues = getSortedVirtualRefinerConfig(this.props.context.request, isSizeRefinement);
            if (sortedConfigValues && slug) {
                const params = MsDyn365.isBrowser && window.location;
                const origin = params && params.origin === 'https://localhost:4000' ? params.origin : requestContext.url.requestUrl.origin;
                slugArray = slug.split('/').splice(1);
                // remove keywords form slugArray
                slugArray = removeKeywords(slugArray, this.props.context.request);
                slug = slugArray.join('/');

                // path of URL
                let path = this.props.context.request.url.requestUrl.pathname.toLowerCase();

                // remove category id {categoryid}.c
                if (categoryId) {
                    path = path?.replace(`/${categoryId}`, '');
                }
                // remove category slug
                pathArray = removeKeywords(path.split('\/').splice(1), this.props.context.request);
                path = pathArray.join('\/');
                const removedCategorySlug = path.split(slug).splice(1)[0];
                let refinerParts: string[] = [];
                if (removedCategorySlug !== '') {
                    refinerParts = decodeURIComponent(removedCategorySlug).split('/');
                }

                if (refinerParts && refinerParts.length > 0 && slugArray && slugArray.length > 0) {
                    // remove duplicates if any from the first url part
                    slugArray.forEach(_slug => {
                        refinerParts.forEach(refinerPart => {
                            // tslint:disable-next-line: no-shadowed-variable
                            const index = slugArray.indexOf(refinerPart);
                            if (index !== -1) {
                                slugArray.splice(index, 1);
                            }
                        });
                    });
                }
                // insert current refiner
                // break final refiners so array contains single values instead of pipe sign values
                const finalRefinerParts: string[] = [];
                refinerParts.forEach(refinerPart => {
                    refinerPart.split('|').map(item => finalRefinerParts.push(item.includes('-') ? item : item.split('-').join(' ')));
                    // finalRefinerParts.push(refinerPart.split('|'));
                });

                refinerParts = finalRefinerParts && flatten(finalRefinerParts);
                const index = refinerParts.indexOf(notification.choiceClicked!.LeftValueBoundString!.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-'));
                if (index !== -1) {
                    refinerParts.splice(index, 1);
                }
                // setup object with refiner values
                const sortedValues: { [key: string]: string[] } = {};
                filteredRefiners?.forEach(refinerSet => {
                    let refinerName = refinerSet.KeyName;
                    if (refinerName) {
                        refinerName = refinerName.toLowerCase();
                        sortedValues[refinerName] = []; // size, brand, price
                    }
                    refinerSet.Values.forEach((refiner) => {
                        refinerParts.forEach((refinerPart) => {
                            if (refinerName && refiner.LeftValueBoundString &&
                                (refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLowerCase().split('-').join(' ')
                                    || refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLowerCase())) {
                                sortedValues[refinerName].push(refiner.LeftValueBoundString.toLowerCase().split(' ').join('-'));
                            } else if (refinerName && refiner.LeftValueBoundString &&
                                refiner.LeftValueBoundString.toLowerCase() === refinerPart.toLowerCase().split('-').join(' ')) {
                                sortedValues[refinerName].push(refinerPart);
                            }
                        });
                    });
                });
                // alpha sort
                // @ts-ignore
                for (const sortedValue of Object.keys(sortedValues)) {
                    sortedValues[sortedValue].length > 0 &&
                        sortedValues[sortedValue].sort((a: string, b: string) => {
                            if (a < b) {
                                return -1;
                            }
                            if (a > b) {
                                return 1;
                            }
                            return 0;
                        });
                }

                // @ts-ignore
                const finalRefiners: string[] = [];
                sortedConfigValues.forEach(sortedConfigValue => {
                    const sortedRefinerName = sortedConfigValue.refinerName.toLowerCase();
                    if (sortedValues[sortedRefinerName] !== undefined && sortedValues[sortedRefinerName].length > 0) {
                        let concatValue: string | string[] = sortedValues[sortedRefinerName];
                        if (sortedValues[sortedRefinerName].length > 1) {
                            concatValue = sortedValues[sortedRefinerName].join('|');
                        } else if (sortedValues[sortedRefinerName].length === 1) {
                            concatValue = sortedValues[sortedRefinerName][0];
                        }
                        // @ts-ignore
                        finalRefiners.push(concatValue.split(' ').join('-')?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-'));
                    }
                });
                data.listPageState.result.currentPageNumber = 0;
                data.listPageState.result.activeFilters = currentRefinementCriteria;
                const finalURL = `${origin}/${slugArray.join('/')}${finalRefiners && finalRefiners.length > 0 ? `/${finalRefiners.join('/')}` : ''
                    }${categoryId ? `/${categoryId}` : ''}${params ? params.search : ''}`;
                window.location.href = finalURL.trim();
            }
        }

        if (notification.parentKeyName.toLowerCase() !== 'price' && MsDyn365.isBrowser) {
            window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria));
        }
        data.listPageState.result.currentPageNumber = 0;
        data.listPageState.result.activeFilters = currentRefinementCriteria;
        if (MsDyn365.isBrowser && notification.parentKeyName.toLowerCase() === 'price' && isCategoryPage) {
            this._isCustomizedRefiner
                ? (window.location.href = buildListPageUrl(fullUrl, currentRefinementCriteria))
                : window.history.pushState({}, '', buildListPageUrl(fullUrl, currentRefinementCriteria));
        }
    };

    private _buildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { data, telemetry } = this.props;
        const fullUrl = getCurrentUrl(this.props.context.request);

        if (!data.listPageState.result) {
            telemetry.warning('[choice-summary-by-category._buildRefinerUrl]ListPageState unavailable, unable to create refiner URL.');
            return fullUrl.href;
        }

        let currentRefinementCriteria = (data.listPageState.result && data.listPageState.result.activeFilters) || [];

        if (!isClearAll) {
            if (!selectedRefiner) {
                telemetry.warning('[choice-summary-by-category._buildRefinerUrl] URL for Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedRefiner, selectedCriterion)
            );
        } else {
            currentRefinementCriteria = [];
        }

        return buildListPageUrl(fullUrl, currentRefinementCriteria);
    };

    private _customBuildRefinerUrlForChoiceSummary = (selectedRefiner: ProductRefinerValue, isClearAll: boolean): string => {
        const { telemetry, context } = this.props;
        const requestContext = context && context.request;
        const isCustomizeRefiner = requestContext.app.config.urlModifications && requestContext.app.config.urlModifications.length > 0;
        const isCategoryPage: boolean = this._getIsCategoryPage();

        const fullUrl = getCurrentUrl(this.props.context.request);
        let currentRefinementCriteria = this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters || [];
        if (this._isCustomizedRefiner && isCategoryPage) {
            currentRefinementCriteria = this.props.data.listPageState.result && this.props.data.listPageState.result.activeVirtualFilters || [];
        }
        if (!currentRefinementCriteria) {
            telemetry.warning('[choice-summary-by-category._onChoiceClicked]ListPageState unavailable, unable to update refiners.');
            return fullUrl.href;
        }

        if (!isClearAll) {
            const selectedChoice = selectedRefiner;
            if (!selectedChoice) {
                telemetry.warning('[choice-summary-by-category._onChoiceClicked] Choice could not be determined');
                return fullUrl.href;
            }
            currentRefinementCriteria = currentRefinementCriteria.filter(
                (selectedCriterion: ProductRefinerValue) => !isMatchingRefinementCriterion(selectedChoice, selectedCriterion)
            );

        } else {
            currentRefinementCriteria = [];
        }

        const virtualRefinersRecordId: number[] = [];
        const isSizeRefinement: boolean | undefined = this.props?.data.category.result?.ExtensionProperties?.find((attr) => attr.Key === 'SizeRefinementEnable')
            ?.Value?.BooleanValue;
        const availableRefiners = getVirtualRefinerNames(this.props.context.request, isSizeRefinement);
        this.props.data.refiners.result && this.props.data.refiners.result.filter((refiner) => {
            if (availableRefiners.includes(refiner.KeyName?.toLowerCase() as string)) {
                virtualRefinersRecordId.push(refiner.RecordId!);
            }

        });

        const categoryIdToken = requestContext.urlTokens.recordId;
        const categoryId = categoryIdToken ? `${categoryIdToken}.c` : undefined;
        if (!this._isCustomizedRefiner) {
            const listPageUrl = buildListPageUrl(fullUrl, currentRefinementCriteria);
            if (!isClearAll && virtualRefinersRecordId && virtualRefinersRecordId.length >= 1) {
                // window.location.href = listPageUrl.trim();
            } else {
                return listPageUrl;
            }
        }
        // below code is to clear virtual refiners if defined in config
        if (isCustomizeRefiner && isCategoryPage && isClearAll && virtualRefinersRecordId && virtualRefinersRecordId.length >= 1) {
            // get category slug
            let slug = this.props.data.category.result && this.props.data.category.result.Slug || '';
            let slugArray: string[];
            const params = MsDyn365.isBrowser && window.location;
            const origin = (params && params.origin === 'https://localhost:4000') ? params.origin : requestContext.url.requestUrl.origin;
            slugArray = slug.split('\/').splice(1);
            // remove keywords form slugArray
            slugArray = removeKeywords(slugArray, this.props.context.request);
            slug = slugArray.join('/');
            const finalURL = `${origin}/${slugArray.join('\/')}${categoryId ? `/${categoryId}` : ''}`;
            return finalURL.trim();
        } else if (isCustomizeRefiner && isCategoryPage && availableRefiners.includes(this._getParentKeyForRefinerValue(selectedRefiner).toLowerCase()) === true) {
            // url builder
            const configVirtualRefinerNames = getVirtualRefinerNames(this.props.context.request, isSizeRefinement);
            const filteredRefiners = this.props.data.refiners.result && this.props.data.refiners.result.filter((refiner) => {
                return refiner.KeyName && configVirtualRefinerNames.indexOf(refiner.KeyName.toLowerCase()) !== -1;
            });

            // get category slug
            let slug = this.props.data.category.result && this.props.data.category.result.Slug || '';
            let slugArray: string[];
            let pathArray: string[];
            const sortedConfigValues = getSortedVirtualRefinerConfig(this.props.context.request, isSizeRefinement);
            if (sortedConfigValues && slug) {
                const params = MsDyn365.isBrowser && window.location;
                const origin = (params && params.origin === 'https://localhost:4000') ? params.origin : requestContext.url.requestUrl.origin;
                slugArray = slug.split('\/').splice(1);
                // remove keywords form slugArray
                slugArray = removeKeywords(slugArray, this.props.context.request);
                slug = slugArray.join('/');

                // path of URL
                let path = this.props.context.request.url.requestUrl.pathname.toLowerCase();

                // remove category id {categoryid}.c
                if (categoryId) {
                    path = path?.replace(`/${categoryId}`, '');
                }
                // remove category slug
                pathArray = removeKeywords(path.split('\/').splice(1), this.props.context.request);
                path = pathArray.join('\/');
                const removedCategorySlug = path.split(slug).splice(1)[0];
                let refinerParts: string[] = [];
                if (removedCategorySlug !== '') {
                    refinerParts = decodeURIComponent(removedCategorySlug).split('\/');
                }

                if (refinerParts && refinerParts.length > 0 && slugArray && slugArray.length > 0) {
                    // remove duplicates if any from the first url part
                    slugArray.forEach((_slug) => {
                        refinerParts.forEach((refinerPart) => {
                            // tslint:disable-next-line: no-shadowed-variable
                            const index = slugArray.indexOf(refinerPart);
                            if (index !== -1) {
                                slugArray.splice(index, 1);
                            }
                        });
                    });
                }
                // insert current refiner
                // break final refiners so array contains single values instead of pipe sign values
                const finalRefinerParts: string[] = [];
                refinerParts.forEach(refinerPart => {
                    refinerPart.split('|').map(item => finalRefinerParts.push(item.includes('-') ? item : item.split('-').join(' ')));
                    // finalRefinerParts.push(refinerPart.split('|'));
                });

                refinerParts = finalRefinerParts && flatten(finalRefinerParts);
                const index = refinerParts.indexOf(selectedRefiner.LeftValueBoundString!.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-'));
                if (index !== -1) {
                    refinerParts.splice(index, 1);
                }
                // setup object with refiner values
                const sortedValues: { [key: string]: string[] } = {};
                filteredRefiners?.forEach((refinerSet) => {
                    let refinerName = refinerSet.KeyName;
                    if (refinerName) {
                        refinerName = refinerName.toLowerCase();
                        sortedValues[refinerName] = []; // size, brand, price
                    }
                    refinerSet.Values.forEach((refiner) => {
                        refinerParts.forEach((refinerPart) => {
                            if (refinerName && refiner.LeftValueBoundString &&
                                (refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLowerCase().split('-').join(' ')
                                    || refiner.LeftValueBoundString.toLowerCase()?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-') === refinerPart.toLowerCase())) {
                                sortedValues[refinerName].push(refiner.LeftValueBoundString.toLowerCase().split(' ').join('-'));
                            } else if (refinerName && refiner.LeftValueBoundString &&
                                refiner.LeftValueBoundString.toLowerCase() === refinerPart.toLowerCase().split('-').join(' ')) {
                                sortedValues[refinerName].push(refinerPart);
                            }
                        });
                    });
                });
                // alpha sort
                // @ts-ignore
                for (const sortedValue of Object.keys(sortedValues)) {
                    sortedValues[sortedValue].length > 0 && sortedValues[sortedValue].sort((a: string, b: string) => {
                        if (a < b) { return -1; }
                        if (a > b) { return 1; }
                        return 0;
                    });
                }

                // @ts-ignore
                const finalRefiners: string[] = [];
                sortedConfigValues.forEach((sortedConfigValue) => {
                    const sortedRefinerName = sortedConfigValue.refinerName.toLowerCase();
                    if (sortedValues[sortedRefinerName] !== undefined && sortedValues[sortedRefinerName].length > 0) {
                        let concatValue: string | string[] = sortedValues[sortedRefinerName];
                        if (sortedValues[sortedRefinerName].length > 1) {
                            concatValue = sortedValues[sortedRefinerName].join('|');
                        } else if (sortedValues[sortedRefinerName].length === 1) {
                            concatValue = sortedValues[sortedRefinerName][0];
                        }
                        // @ts-ignore
                        finalRefiners.push(concatValue.split(' ').join('-')?.replace(/\&/g, 'and')?.replace(regExSpecialChars(requestContext.app.config.canonicalUrlspecialCharIncludeList), '-'));
                    }
                });
                const finalURL = `${origin}/${slugArray.join('\/')}${finalRefiners && finalRefiners.length > 0 ? `/${finalRefiners.join('\/')}` : ''}${categoryId ? `/${categoryId}` : ''}${params ? params.search : fullUrl && fullUrl.search ? fullUrl.search : ''}`;
                return finalURL.trim();
            }

        }
        return buildListPageUrl(fullUrl, currentRefinementCriteria);
    };

    private _getViewport(): GridSize {
        if (MsDyn365.isBrowser) {
            return isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        }
        return isMobile({ variant: VariantType.Viewport, context: this.props.context.request });
    }

    private _formatPrice(amount: string | undefined, currency: string | undefined): string {
        if (!amount || !currency) {
            this.props.telemetry.trace('[choice-summary] could not format price');
            return amount || '';
        }
        const priceAmount = (amount && Number(amount)) || 0;
        const locale = get(this.props, 'context.request.locale', 'en-US');
        let result: string;

        try {
            result = new Intl.NumberFormat(locale, {
                style: 'currency',
                currencyDisplay: 'symbol',
                currency: currency,
                minimumFractionDigits: 0
            }).format(priceAmount);
        } catch (e) {
            result = `${priceAmount} ${currency} `;
            this.props.telemetry.warning(`Failed to format price for ${result}: ${e} `);
        }
        return result;
    }
    private _getKeyForRefinerValue(productRefinerValue: ProductRefinerValue): string {
        const { choiceFormat, choiceRangeValueFormat } = this.props.resources;
        const refinerHierarchy = this.props.data.refiners.result;
        const { telemetry } = this.props;
        const overallFormat = choiceFormat || '{1}';
        const rangeFormat = choiceRangeValueFormat;
        let refinerName = '';
        if (refinerHierarchy && refinerHierarchy.find) {
            const parent = refinerHierarchy.find(
                (hierarchy: IProductRefinerHierarchy) =>
                    !!hierarchy.Values.find((value: ProductRefinerValue) => isMatchingRefinementCriterion(value, productRefinerValue))
            );
            if (!parent) {
                telemetry.warning('[choice-summary] could not find parent of selected refiner value');
            } else {
                refinerName = parent.KeyName || '';
            }
        }

        let refinerValueName: string;
        if (isRangeType(productRefinerValue.DataTypeValue)) {
            refinerValueName = rangeFormat
                ?.replace('{0}', this._formatPrice(productRefinerValue.LeftValueBoundString, productRefinerValue.UnitText))
                ?.replace('{1}', this._formatPrice(productRefinerValue.RightValueBoundString, productRefinerValue.UnitText));
        } else {
            refinerValueName = productRefinerValue.LeftValueBoundLocalizedString || productRefinerValue.LeftValueBoundString || '';
        }

        return overallFormat?.replace('{0}', refinerName)?.replace('{1}', refinerValueName);
    }
    private _getParentKeyForRefinerValue(productRefinerValue: ProductRefinerValue): string {
        const { telemetry } = this.props;
        const refinerHierarchy = this.props.data.refiners.result;
        let refinerName = '';
        if (refinerHierarchy && refinerHierarchy.find) {
            const parent = refinerHierarchy.find(
                (hierarchy: IProductRefinerHierarchy) =>
                    !!hierarchy.Values.find((value: ProductRefinerValue) => isMatchingRefinementCriterion(value, productRefinerValue))
            );
            if (!parent) {
                telemetry.warning('[choice-summary] could not find parent of selected refiner value');
            } else {
                refinerName = parent.KeyName || '';
            }
        }

        return refinerName;
    }

    private _toggleModal(): void {
        const filterCategoriesArr: string[] = [];
        const filterItemsArr: string[] = [];
        const selectedChoices = this.props.data.listPageState.result && this.props.data.listPageState.result.activeFilters;
        selectedChoices &&
            selectedChoices.length > 0 &&
            selectedChoices.map((selectedRefiner: ProductRefinerValue) => {
                const obj = {
                    key: this._getKeyForRefinerValue(selectedRefiner),
                    parentName: this._getParentKeyForRefinerValue(selectedRefiner)
                };
                filterCategoriesArr.push(obj.parentName);
                filterItemsArr.push(obj.key);
            });
        const uniqueFilterCategories = [...new Set(filterCategoriesArr)];
        const filterCategories = uniqueFilterCategories.join('|');
        const filterItems = filterItemsArr.join('|');
        // @ts-ignore
        if (MsDyn365.isBrowser && utag && this.state.modalIsOpen) {
            // @ts-ignore
            // tslint:disable-next-line: object-literal-key-quotes
            utag.link({
                event: 'PLP-filter-action',
                event_category: 'PLP Filter Action',
                event_action: `${filterCategories} `,
                event_label: `${filterItems} `,
                event_noninteraction: 'false'
            });
        }
        this.setState({
            modalIsOpen: !this.state.modalIsOpen
        });
    }

    private _updateViewport(): void {
        this._viewport = this._getViewport();
        if (this.state.modalIsOpen && !this.isMobile) {
            this._toggleModal();
        }
    }

    private _getIsCategoryPage(): boolean {
        const { context } = this.props;
        const requestContext = context && context.request;
        const pageType =
            requestContext.urlTokens.pageType === 'Category' || (requestContext.query && requestContext.query.categoryId)
                ? 'Category'
                : 'Search';
        return pageType?.toLowerCase() === 'search' ? false : true;
    }

    private _removePriceAndVirtualRefiner(currentRefinementCriteria: ProductRefinerValue[]): ProductRefinerValue[] {
        const activeRefiners = currentRefinementCriteria;
        const priceRefiner = activeRefiners?.find(refiner => refiner.DataTypeValue === 1);
        if (priceRefiner) {
            currentRefinementCriteria = activeRefiners?.filter(
                selectedCriterion => !isMatchingRefinementCriterion(priceRefiner, selectedCriterion)
            );
        }
        let withoutVirtualRefiners = [...currentRefinementCriteria];
        if (this._isCustomizedRefiner) {
            const allVirtualRefinersValue = currentRefinementCriteria.filter(obj =>
                this.state.allVirtualRefiners.includes(obj?.LeftValueBoundString?.toLowerCase())
            );
            let updatedRefinersArr = [...currentRefinementCriteria];
            allVirtualRefinersValue.map(refinerValue => {
                const selectedObj = currentRefinementCriteria?.find(
                    refiner => refiner.LeftValueBoundString?.toLowerCase() === refinerValue.LeftValueBoundString?.toLowerCase()
                );
                if (selectedObj) {
                    withoutVirtualRefiners = updatedRefinersArr?.filter(
                        selectedCriterion => !isMatchingRefinementCriterion(selectedObj, selectedCriterion)
                    );
                }
                updatedRefinersArr = [...withoutVirtualRefiners];
            });
            if (MsDyn365.isBrowser) {
                window.history.pushState({}, '', buildListPageUrl(getCurrentUrl(this.props.context.request), withoutVirtualRefiners));
            }
        }
        return withoutVirtualRefiners;
    }

}
